import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan,
  faBed,
  faClipboardList,
  faSpinner,
  faTrash,
  faUser,
  faUserFriends,
  faWeight,
} from '@fortawesome/free-solid-svg-icons';
import { resolveAvatarUrl } from '../../utils/avatar';

const CardShell = styled.div`
  background: #ffffff;
  padding: 1rem;
  border-bottom: 1px solid #e8eff6;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  overflow: hidden;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 480px) {
    padding: 0.85rem 0.8rem;
  }
`;

const FriendHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 480px) {
    gap: 0.65rem;
    margin-bottom: 0.62rem;
  }
`;

const Avatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.25rem;
  font-family: 'Poppins', sans-serif;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
    font-size: 1.1rem;
  }
`;

const FriendInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FriendName = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 1.1rem;
  color: #1A2E40;
  margin: 0 0 0.25rem 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const FriendUsername = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 0.85rem;
  color: #64748b;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const FriendMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  padding: 0.45rem 0;
  margin-bottom: 0.75rem;
  max-width: 100%;
  overflow: hidden;
  border-top: 1px dashed #e3ebf3;
  border-bottom: 1px dashed #e3ebf3;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.4rem;
    padding: 0.42rem 0;
    margin-bottom: 0.65rem;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: #64748b;
  font-family: 'Roboto', sans-serif;
  flex-wrap: wrap;
  min-width: 0;
  word-break: break-word;

  span.value {
    font-weight: 600;
    color: #1A2E40;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const FriendActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  max-width: 100%;

  @media (max-width: 480px) {
    gap: 0.35rem;
  }

  @media (max-width: 340px) {
    gap: 0.35rem;
  }
`;

const ActionButton = styled.button`
  width: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.52rem 0.74rem;
  border: 1px solid #d3e1ee;
  border-radius: 999px;
  background: #ffffff;
  color: #36536e;
  font-family: 'Roboto', sans-serif;
  font-size: 0.77rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s ease;
  text-decoration: none;
  min-height: 40px;
  min-width: 0;
  line-height: 1.2;

  svg {
    font-size: 0.95rem;
    flex-shrink: 0;
  }

  @media (max-width: 480px) {
    min-height: 38px;
    padding: 0.52rem 0.45rem;
    font-size: 0.75rem;
    gap: 0.35rem;
    border-radius: 999px;

    svg {
      font-size: 0.82rem;
    }
  }

  @media (max-width: 340px) {
    min-height: 36px;
    padding: 0.45rem 0.65rem;
  }

  &:disabled {
    background: #f4f7fa;
    color: #9aa8b5;
    border-color: #dbe5ef;
    cursor: not-allowed;
    transform: none;
  }

  &.delete {
    border-color: #f4c8c7;
    background: #fff4f4;
    color: #be2f2b;
  }

  &.block {
    border-color: #f2d8b5;
    background: #fff7ed;
    color: #9a4f0b;
  }
`;

const FriendsHeaderShell = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: linear-gradient(135deg, #1A2E40 0%, #2c5282 100%);
  box-shadow: 0 8px 18px rgba(26, 46, 64, 0.16);
  gap: 0.75rem;

  @media (max-width: 480px) {
    margin-bottom: 1rem;
    padding: 0.75rem 0.85rem;
  }
`;

const FriendsHeaderTitle = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 1.08rem;
  font-weight: 800;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: #ffffff;
    flex-shrink: 0;
  }
`;

const FriendsHeaderCount = styled.span`
  background: rgba(255, 255, 255, 0.16);
  color: #ffffff;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'Roboto Mono', monospace;
  flex-shrink: 0;
`;

const parseBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return ['true', '1', 'yes', 'oui'].includes(normalized);
  }
  return false;
};

const getInitials = (user) => {
  if (!user) return '??';
  if (user.firstname && user.lastname) return `${user.firstname[0]}${user.lastname[0]}`.toUpperCase();
  if (user.username) return user.username.substring(0, 2).toUpperCase();
  return '??';
};

const getDisplayName = (user) => {
  if (!user) return 'Utilisateur inconnu';
  if (user.firstname && user.lastname) return `${user.firstname} ${user.lastname}`;
  return user.username || 'Utilisateur';
};

export const FriendsDemoHeader = ({ count = 0 }) => {
  return (
    <FriendsHeaderShell>
      <FriendsHeaderTitle>
        <FontAwesomeIcon icon={faUserFriends} />
        Mes amis
      </FriendsHeaderTitle>
      <FriendsHeaderCount>{count}</FriendsHeaderCount>
    </FriendsHeaderShell>
  );
};

export const FriendCardRow = ({
  friend,
  currentUser,
  blockingId,
  onBlockFriend,
  onRemoveFriend,
  mode = 'app',
  demoHighlight = null,
  className,
}) => {
  const user = useMemo(() => {
    if (!friend) return null;
    if (currentUser?.id) return friend.sender?.id === currentUser.id ? friend.receiver : friend.sender;
    if (currentUser?.username) return friend.sender?.username === currentUser.username ? friend.receiver : friend.sender;
    return friend.receiver || friend.sender;
  }, [currentUser?.id, currentUser?.username, friend]);

  const hasPublicJournal = parseBoolean(user?.journal?.public);
  const hasPublicWeight = parseBoolean(user?.weightSettings?.weightHistoryPublic ?? user?.weightSettings?.historyPublic);
  const hasPublicSleep = parseBoolean(user?.sleepSettings?.sleepHistoryPublic ?? user?.sleepSettings?.historyPublic);
  const avatarUrl = resolveAvatarUrl(user?.avatar);

  const since = friend?.createdAt
    ? new Date(friend.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  const isDemo = mode === 'demo';
  const blockDisabled = isDemo ? true : blockingId === friend?.id;
  const isDemoActive = (key) => isDemo && demoHighlight === key;

  const demoActionClass = (key, extra = '') => {
    const classes = [extra, `friend-action-${key}`];
    if (isDemoActive(key)) classes.push('demo-simulated-active');
    return classes.filter(Boolean).join(' ');
  };

  const renderDemoAction = (key, icon, label, className = '') => (
    <ActionButton
      type="button"
      tabIndex={-1}
      aria-hidden="true"
      className={demoActionClass(key, `${className} demo-button`)}
      style={{ pointerEvents: 'none' }}
    >
      <FontAwesomeIcon icon={icon} />
      {label}
    </ActionButton>
  );

  return (
    <CardShell className={className}>
      <FriendHeader>
        <Avatar>
          {avatarUrl ? <img src={avatarUrl} alt="Avatar" /> : getInitials(user)}
        </Avatar>
        <FriendInfo>
          <FriendName>{getDisplayName(user)}</FriendName>
          <FriendUsername>@{user?.username || 'inconnu'}</FriendUsername>
        </FriendInfo>
      </FriendHeader>

      <FriendMeta>
        <MetaItem>
          Ami depuis : <span className="value">{since}</span>
        </MetaItem>
      </FriendMeta>

      <FriendActions>
        {isDemo ? (
          <>
            {renderDemoAction('profile', faUser, 'Profil')}
            {renderDemoAction('journal', faClipboardList, 'Journal')}
            {renderDemoAction('sleep', faBed, 'Sommeil', 'sleep')}
            {renderDemoAction('weight', faWeight, 'Poids', 'weight')}
            {renderDemoAction('block', faBan, 'Bloquer', 'block')}
            {renderDemoAction('delete', faTrash, 'Supprimer', 'delete')}
          </>
        ) : (
          <>
            <ActionButton as={Link} to={`/friends/${user?.username}/profile`} aria-label={`Voir le profil de ${user?.username || 'cet ami'}`}>
              <FontAwesomeIcon icon={faUser} />
              Profil
            </ActionButton>

            {hasPublicJournal ? (
              <ActionButton as={Link} to={`/friends/${user?.username}/journal`} aria-label={`Voir le journal de ${user?.username || 'cet ami'}`}>
                <FontAwesomeIcon icon={faClipboardList} />
                Journal
              </ActionButton>
            ) : (
              <ActionButton disabled aria-label={`Le journal de ${user?.username || 'cet ami'} est privé`} title="Journal privé: accès indisponible">
                <FontAwesomeIcon icon={faClipboardList} />
                Journal
              </ActionButton>
            )}

            {hasPublicSleep ? (
              <ActionButton as={Link} to={`/friends/${user?.username}/sleep`} className="sleep" aria-label={`Voir le suivi du sommeil de ${user?.username || 'cet ami'}`}>
                <FontAwesomeIcon icon={faBed} />
                Sommeil
              </ActionButton>
            ) : (
              <ActionButton className="sleep" disabled aria-label={`Le suivi du sommeil de ${user?.username || 'cet ami'} est privé`} title="Suivi sommeil privé: accès indisponible">
                <FontAwesomeIcon icon={faBed} />
                Sommeil
              </ActionButton>
            )}

            {hasPublicWeight ? (
              <ActionButton as={Link} to={`/friends/${user?.username}/weight`} className="weight" aria-label={`Voir le suivi du poids de ${user?.username || 'cet ami'}`}>
                <FontAwesomeIcon icon={faWeight} />
                Poids
              </ActionButton>
            ) : (
              <ActionButton className="weight" disabled aria-label={`Le suivi du poids de ${user?.username || 'cet ami'} est privé`} title="Suivi poids privé: accès indisponible">
                <FontAwesomeIcon icon={faWeight} />
                Poids
              </ActionButton>
            )}

            <ActionButton
              className="block"
              onClick={() => onBlockFriend?.(friend)}
              disabled={blockDisabled}
              aria-label={`Bloquer ${user?.username || 'cet ami'}`}
              type="button"
            >
              <FontAwesomeIcon icon={blockDisabled ? faSpinner : faBan} spin={blockDisabled} />
              {blockDisabled ? 'Blocage...' : 'Bloquer'}
            </ActionButton>

            <ActionButton
              className="delete"
              onClick={() => onRemoveFriend?.(friend)}
              aria-label={`Supprimer ${user?.username || 'cet ami'} de vos amis`}
              type="button"
            >
              <FontAwesomeIcon icon={faTrash} />
              Supprimer
            </ActionButton>
          </>
        )}
      </FriendActions>
    </CardShell>
  );
};

